import { useEffect, useCallback, useRef } from 'react';
import { checkKey } from '../utils/keys';

export interface KeydownProps {
  [key: string]:
    | ((e: KeyboardEvent) => void)
    | boolean
    | undefined
    | number
    | string[];
  number?: ((e: KeyboardEvent) => void) | undefined;
  letter?: ((e: KeyboardEvent) => void) | undefined;
  remove?: ((e: KeyboardEvent) => void) | undefined;
  isActive: boolean;
  debounce?: number;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  allowedInputs?: string[];
}

interface KeyState {
  pressed: boolean;
  interval: NodeJS.Timeout | null;
  timestamp: number;
}

// Define opposite key pairs to handle conflicts
const oppositeKeys = {
  arrowleft: 'arrowright',
  arrowright: 'arrowleft',
  arrowup: 'arrowdown',
  arrowdown: 'arrowup',
};

function useKeydown(props: KeydownProps): void {
  const keyStates = useRef<{ [key: string]: KeyState }>({});
  const activeKeys = useRef<Set<string>>(new Set());

  const clearKeyState = useCallback((key: string) => {
    if (keyStates.current[key]) {
      if (keyStates.current[key].interval) {
        clearInterval(keyStates.current[key].interval);
      }
      keyStates.current[key] = {
        pressed: false,
        interval: null,
        timestamp: 0,
      };
      activeKeys.current.delete(key);
    }
  }, []);

  const handleKeydown = useCallback(
    (e: KeyboardEvent): void => {
      if (!props.isActive) return;

      console.log(e, '---');

      const key = checkKey(e);
      const specialKey = checkKey(e);
      const currentTime = Date.now();

      const isAllowedInput = props.allowedInputs?.some((selector) =>
        (e.target as Element)?.matches?.(selector)
      );

      if (props.preventDefault && !isAllowedInput) {
        e.preventDefault();
      }

      if (props.stopPropagation) {
        e.stopPropagation();
      }

      // Initialize key state if it doesn't exist
      if (!keyStates.current[key]) {
        keyStates.current[key] = {
          pressed: false,
          interval: null,
          timestamp: 0,
        };
      }

      // Handle opposite key conflicts
      const oppositeKey = oppositeKeys[key as keyof typeof oppositeKeys];
      if (oppositeKey && keyStates.current[oppositeKey]?.pressed) {
        // If the opposite key was pressed more recently, ignore this keypress
        if (
          keyStates.current[oppositeKey].timestamp >
          keyStates.current[key].timestamp
        ) {
          return;
        }
        // Clear the opposite key's state
        clearKeyState(oppositeKey);
      }

      // Update key state
      keyStates.current[key] = {
        pressed: true,
        interval: keyStates.current[key].interval,
        timestamp: currentTime,
      };
      activeKeys.current.add(key);

      const executeKeyHandler = (handlerKey: string) => {
        if (typeof props[handlerKey] === 'function') {
          (props[handlerKey] as (e: KeyboardEvent) => void)(e);
        }
      };

      // Only execute and set interval if this key isn't already being handled
      if (!keyStates.current[key].interval) {
        // Execute immediately
        if (/^\d$/.test(key) && props.number) {
          executeKeyHandler('number');
        } else if (/^[a-z]$/.test(key) && props.letter) {
          executeKeyHandler('letter');
        } else if (specialKey && props[specialKey]) {
          executeKeyHandler(specialKey);
        }

        // Set up interval for repeated execution
        keyStates.current[key].interval = setInterval(() => {
          // If key is no longer pressed or no longer the most recent opposite key
          if (
            !keyStates.current[key].pressed ||
            (oppositeKey &&
              keyStates.current[oppositeKey]?.pressed &&
              keyStates.current[oppositeKey].timestamp >
                keyStates.current[key].timestamp)
          ) {
            clearKeyState(key);
            return;
          }

          if (/^\d$/.test(key) && props.number) {
            executeKeyHandler('number');
          } else if (/^[a-z]$/.test(key) && props.letter) {
            executeKeyHandler('letter');
          } else if (specialKey && props[specialKey]) {
            executeKeyHandler(specialKey);
          }
        }, props.debounce || 100);
      }
    },
    [props, clearKeyState]
  );

  const handleKeyup = useCallback(
    (e: KeyboardEvent): void => {
      const key = checkKey(e);

      // Clear the released key's state
      clearKeyState(key);

      // Check if there's an opposite key that was previously suppressed
      const oppositeKey = oppositeKeys[key as keyof typeof oppositeKeys];
      if (oppositeKey && keyStates.current[oppositeKey]?.pressed) {
        // Re-trigger the opposite key if it's still physically pressed
        const event = new KeyboardEvent('keydown', { key: oppositeKey });
        handleKeydown(event);
      }
    },
    [clearKeyState, handleKeydown]
  );

  useEffect(() => {
    if (props.isActive) {
      window.addEventListener('keydown', handleKeydown);
      window.addEventListener('keyup', handleKeyup);
    }

    return () => {
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('keyup', handleKeyup);
      // Clean up all intervals and states
      Array.from(activeKeys.current).forEach(clearKeyState);
    };
  }, [props.isActive, handleKeydown, handleKeyup, clearKeyState]);
}

export { useKeydown };
